// lib/workspace-annotations.ts
import { Workspace, WorkspaceVersion } from '#types/types';

export const normalizeWorkspaceAnnotationOpaque = (value: unknown): boolean => value === true;

export const resolveWorkspaceAnnotationOpaqueRollback = (
  persistedWorkspace: Pick<Workspace, 'annotationOpaque'> | null | undefined,
  payloadIsCurrent: boolean,
): boolean | null => (
  payloadIsCurrent && persistedWorkspace
    ? normalizeWorkspaceAnnotationOpaque(persistedWorkspace.annotationOpaque)
    : null
);

export const resolveWorkspaceAnnotationOpacity = (annotationOpaque: unknown): 1 | 0.8 => (
  annotationOpaque === true ? 1 : 0.8
);

export const drawWorkspaceAnnotationOverlay = (
  context: CanvasRenderingContext2D,
  source: CanvasImageSource,
  sx: number,
  sy: number,
  sWidth: number,
  sHeight: number,
  destinationWidth: number,
  destinationHeight: number,
  annotationOpaque: unknown,
): void => {
  const previousGlobalAlpha = context.globalAlpha;
  context.globalAlpha = resolveWorkspaceAnnotationOpacity(annotationOpaque);
  try {
    context.drawImage(
      source,
      sx,
      sy,
      sWidth,
      sHeight,
      0,
      0,
      destinationWidth,
      destinationHeight,
    );
  } finally {
    context.globalAlpha = previousGlobalAlpha;
  }
};

const normalizeVersionId = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

export const resolveWorkspaceAnnotationVersionId = (
  workspace: Workspace | null | undefined
): string | null => {
  if (!workspace) return null;
  return normalizeVersionId(workspace.activeVersionId);
};

export const findWorkspaceVersionById = (
  workspace: Workspace | null | undefined,
  versionId: string | null | undefined
): WorkspaceVersion | null => {
  if (!workspace) return null;
  const normalizedVersionId = normalizeVersionId(versionId);
  if (!normalizedVersionId) return null;
  return (
    (workspace.versions ?? []).find((version) => version.id === normalizedVersionId) ??
    null
  );
};

export const resolveWorkspaceAnnotationMask = (
  workspace: Workspace | null | undefined
): ImageData | null => {
  if (!workspace) return null;
  const targetVersionId = resolveWorkspaceAnnotationVersionId(workspace);
  if (!targetVersionId) return null;

  const targetVersion = findWorkspaceVersionById(workspace, targetVersionId);
  return targetVersion?.annotationMask ?? null;
};
